import { memo, useCallback, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import { Typography, Steps, Button, Form, Spin } from 'antd';

import Breadcrumb from '../../../../components/common/breadcrumb';

import JobDetailsStep from '../../components/job-details-step';
import { locationFromForm } from '../../utils/jobLocation';
import SkillsEducationStep from '../../components/skills-education-step';
import JobCustomerField from '../../components/job-customer-field';
import { usePermissions } from '../../../../context/permission.context';
import Layout from '../../../../components/layout';
import { Step } from '../../types/index.types';
import styles from './index.module.css';
import { JobPayload } from '../../../../fsm/jobs';
import { User } from '../../../../fsm/users';
import InterviewerSetup from '../../components/interviewer-setup';
import PostingWatcher from '../../components/interviewer-setup/PostingWatcher';
import { isInterviewerSetupEnabled } from '../../interviewer/flag';
import { useInterviewer } from '../../interviewer/useInterviewer';
import type {
  InterviewerConfig,
  Posting,
} from '../../services/interviewer.types';

const { Title } = Typography;

interface Props {
  user?: User;
  isLoading: boolean;
  autoPopulatedValues: Record<
    string,
    Array<string> | number | string | Record<string, unknown>
  >;
  /**
   * With the interviewer setup on, the recruiter's shaped interviewer comes
   * too, to be saved against the new job. Absent when they left Rio's draft
   * as it was: Rio's default needs nothing saved.
   */
  onCreateJob: (job: JobPayload, interviewer?: InterviewerConfig) => void;
  onGetAutoPopulatedValues: (title: string, description: string) => void;
  onResetAutoPopulatedValues: () => void;
}

const breadcrumbItems = [
  {
    title: <Link to="..">Job list</Link>,
  },
  {
    title: 'Add job',
  },
];

const steps = [
  {
    title: 'Job Details',
  },
  {
    title: 'Skills & Education',
  },
];

const EMPTY_POSTING: Posting = { title: '', description: '', skills: [] };

const DRAFT_NOTES = {
  empty: 'Rio drafts your interviewer from the title and description.',
  drafting: 'Rio is drafting your interviewer from this posting…',
  ready: 'Rio has drafted your interviewer. You can shape it in the last step.',
  error:
    "Rio couldn't draft your interviewer yet. You can try again in the last step.",
};

const CreateJob = ({
  isLoading,
  user,
  autoPopulatedValues,
  onCreateJob,
  onGetAutoPopulatedValues,
  onResetAutoPopulatedValues,
}: Props) => {
  const [step, setStep] = useState<number>(Step.JobDetails);
  const [canPost, setCanPost] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [form] = Form.useForm();
  // A Super Admin belongs to no customer, so the backend needs to be told which
  // one this job is for. Every other role posts to their own.
  const { canSeeJobPostCustomerField: needsCustomer } = usePermissions();
  const [customerId, setCustomerId] = useState<number | undefined>();
  const [customerMissing, setCustomerMissing] = useState(false);
  // Rio's interviewer, drafted from the posting as it's written. Off unless
  // REACT_APP_INTERVIEWER_SETUP is set: then nothing here runs or renders.
  const interviewerOn = isInterviewerSetupEnabled();
  const [posting, setPosting] = useState<Posting>(EMPTY_POSTING);
  const interviewer = useInterviewer({
    posting,
    autoDraft: true,
    enabled: interviewerOn,
  });
  const wizardSteps = interviewerOn
    ? [...steps, { title: 'Interviewer' }]
    : steps;

  useEffect(() => {
    onResetAutoPopulatedValues();
    form.resetFields();

    form.setFieldsValue({
      minSalary: 50000,
      maxSalary: 100000,
    });

    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    if (!Object.keys(autoPopulatedValues).length) return;

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const patch: Record<string, any> = {};

    const certs = autoPopulatedValues.certificationsAndLicenses as Record<
      string,
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      any
    >;

    // simple mappings
    if (autoPopulatedValues.location) {
      patch.address = autoPopulatedValues.location;
    }

    if (autoPopulatedValues.employmentType) {
      patch.workplaceTypes = autoPopulatedValues.employmentType;
    }

    if (autoPopulatedValues.hasManagementExperience) {
      patch.hasManagementExperience =
        autoPopulatedValues.hasManagementExperience;
    }

    if (autoPopulatedValues.educationList) {
      patch.educationList = autoPopulatedValues.educationList;
    }

    if (autoPopulatedValues.jobTitle) {
      patch.title = autoPopulatedValues.jobTitle;
    }

    if (autoPopulatedValues.employerName) {
      patch.employerName = autoPopulatedValues.employerName;
    }

    // arrays
    if (autoPopulatedValues.altJobTitles) {
      patch.altJobTitles = autoPopulatedValues.altJobTitles;
    }

    if (autoPopulatedValues.preferredSkills) {
      patch.preferredSkills = autoPopulatedValues.preferredSkills;
    }

    if (autoPopulatedValues.requiredSkills) {
      patch.requiredSkills = autoPopulatedValues.requiredSkills;
    }

    if (certs?.certifications) {
      patch.certifications = certs.certifications;
    }

    if (certs?.licenses) {
      patch.licensures = certs.licenses;
    }

    // experience
    if (autoPopulatedValues.minTotalExperience) {
      patch.minTotalExperience = autoPopulatedValues.minTotalExperience;
    }

    if (autoPopulatedValues.maxTotalExperience) {
      patch.maxTotalExperience = autoPopulatedValues.maxTotalExperience;
    }

    if (autoPopulatedValues.minIndustryExperience) {
      patch.minIndustryExperience = autoPopulatedValues.minIndustryExperience;
    }

    if (autoPopulatedValues.maxIndustryExperience) {
      patch.maxIndustryExperience = autoPopulatedValues.maxIndustryExperience;
    }

    // ✅ salary logic (fixed + improved)
    const salary = autoPopulatedValues.salaryRange as
      { min?: number; max?: number } | undefined;

    if (salary) {
      if (salary.min && salary.max) {
        patch.minSalary = salary.min;
        patch.maxSalary = salary.max;
      } else if (salary.min) {
        // 🔥 only min exists → set both
        patch.minSalary = salary.min;
        patch.maxSalary = salary.min;
      } else if (salary.max) {
        // 🔥 only max exists → set both
        patch.minSalary = salary.max;
        patch.maxSalary = salary.max;
      }
    }

    // apply once
    if (Object.keys(patch).length) {
      form.setFieldsValue(patch);
    }
  }, [autoPopulatedValues, isInitialized, form]);

  const onFinish = useCallback(() => {
    console.log('finish');
  }, []);

  const onFieldsChange = useCallback(
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    (changedFields: any, allFields: any) => {
      if (
        changedFields[0].name[0] === 'title' ||
        changedFields[0].name[0] === 'description'
      ) {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const title = allFields.find((item: any) => item.name[0] === 'title');
        const description = allFields.find(
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          (item: any) => item.name[0] === 'description',
        );
        setCanPost(title.value && description.value);
      }
    },
    [setCanPost],
  );

  const handleStepChange = useCallback(
    (step: Step) => {
      setStep(step);
      const element = document.getElementById('content');
      element?.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setStep],
  );

  const handleSaveJob = useCallback(() => {
    if (needsCustomer && customerId === undefined) {
      setCustomerMissing(true);
      return;
    }
    const job = {
      ...(needsCustomer ? { customerId } : {}),
      title: form.getFieldValue('title'),
      description: form.getFieldValue('description'),
      employerName: form.getFieldValue('employerName') || null,
      workplaceTypes: form.getFieldValue('workplaceTypes')
        ? [form.getFieldValue('workplaceTypes')]
        : null,
      altJobTitles: form.getFieldValue('altJobTitles') || null,
      location: {
        ...locationFromForm({
          address: form.getFieldValue('address'),
          countryCode: form.getFieldValue('countryCode'),
          city: form.getFieldValue('city'),
          state: form.getFieldValue('state'),
        }),
        zipCode: '',
        latitude: 0,
        longitude: 0,
        radius: 0,
      },
      minSalary: form.getFieldValue('minSalary') || null,
      maxSalary: form.getFieldValue('maxSalary') || null,
      requiredSkills: form.getFieldValue('requiredSkills') || null,
      preferredSkills: form.getFieldValue('preferredSkills') || null,
      minTotalExperience: form.getFieldValue('minTotalExperience') || null,
      maxTotalExperience: form.getFieldValue('maxTotalExperience') || null,
      minIndustryExperience:
        form.getFieldValue('minIndustryExperience') || null,
      maxIndustryExperience:
        form.getFieldValue('maxIndustryExperience') || null,
      hasManagementExperience:
        form.getFieldValue('hasManagementExperience') || null,
      educationList: form.getFieldValue('educationList'),
      certification: {
        certifications: form.getFieldValue('certifications') || null,
        licensures: form.getFieldValue('licensures') || null,
      },
    };

    const shaped =
      interviewerOn && interviewer.touched && interviewer.config
        ? interviewer.config
        : undefined;
    if (shaped) {
      onCreateJob(job, shaped);
    } else {
      onCreateJob(job);
    }
  }, [
    form,
    onCreateJob,
    needsCustomer,
    customerId,
    interviewerOn,
    interviewer.touched,
    interviewer.config,
  ]);

  return (
    <Layout user={user}>
      <Breadcrumb
        items={breadcrumbItems}
        renderRight={
          <Button
            type="primary"
            shape="round"
            disabled={!canPost || (needsCustomer && customerId === undefined)}
            onClick={handleSaveJob}
            loading={isLoading}
          >
            Post job
          </Button>
        }
      />
      <div className={styles.contentWrapper}>
        <Title level={isMobile ? 5 : 3} className={styles.title}>
          Add job
        </Title>
        <div className={styles.formWrapper}>
          {needsCustomer && (
            <JobCustomerField
              value={customerId}
              onChange={(id) => {
                setCustomerId(id);
                setCustomerMissing(false);
              }}
              showRequired={customerMissing}
            />
          )}
          <Steps
            size="small"
            current={step}
            items={wizardSteps}
            type={isMobile ? 'inline' : 'default'}
          />
          {interviewerOn && (
            <PostingWatcher form={form} onChange={setPosting} />
          )}
          {interviewerOn && step !== Step.Interviewer && (
            <p className={styles.draftNote} role="status">
              <span
                className={`${styles.draftDot} ${
                  interviewer.status === 'ready' ? styles.draftDotReady : ''
                }`}
                aria-hidden
              />
              {DRAFT_NOTES[interviewer.status]}
            </p>
          )}
          <div className={styles.formInnerWrapper}>
            <Form
              form={form}
              layout="vertical"
              style={{ width: '100%' }}
              onFinish={onFinish}
              onFieldsChange={onFieldsChange}
              autoComplete="off"
            >
              {step === 0 && (
                <Spin
                  spinning={isLoading}
                  tip="Auto-filling fields with AI-generated suggestions…"
                >
                  <JobDetailsStep
                    form={form}
                    onStepChange={handleStepChange}
                    onGetAutoPopulatedValues={onGetAutoPopulatedValues}
                  />
                </Spin>
              )}
              {step === 1 && (
                <SkillsEducationStep
                  form={form}
                  onStepChange={handleStepChange}
                  isLoading={isLoading}
                  onSubmitForm={handleSaveJob}
                  onNext={
                    interviewerOn
                      ? () => handleStepChange(Step.Interviewer)
                      : undefined
                  }
                />
              )}
            </Form>
          </div>
        </div>
        {interviewerOn && step === Step.Interviewer && (
          <div className={styles.interviewerStep}>
            <div className={styles.interviewerIntro}>
              <Title level={4} className={styles.interviewerTitle}>
                Shape your interviewer
              </Title>
              <p className={styles.interviewerLede}>
                Rio drafted this from your posting. Adjust it directly, or tell
                Rio what you&rsquo;re looking for in your own words.
              </p>
            </div>
            <InterviewerSetup
              state={interviewer}
              posting={posting}
              status={
                interviewer.touched
                  ? 'Edited by you'
                  : 'Drafted from your posting'
              }
              notice={
                interviewer.postingChanged ? (
                  <div className={styles.redraft}>
                    <span>
                      The posting has changed since Rio&rsquo;s draft.
                      Redrafting replaces your changes.
                    </span>
                    <Button
                      shape="round"
                      onClick={() => interviewer.draft(posting)}
                    >
                      Redraft from the posting
                    </Button>
                  </div>
                ) : undefined
              }
            />
            <div className={styles.interviewerActions}>
              <Button
                size="large"
                type="text"
                shape="round"
                onClick={() => handleStepChange(Step.SkillsEducation)}
              >
                Previous
              </Button>
              <Button
                type="primary"
                size="large"
                shape="round"
                loading={isLoading}
                disabled={needsCustomer && customerId === undefined}
                onClick={handleSaveJob}
              >
                Post job
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default memo(CreateJob);
